import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { API, useApp } from "../context";

const DISMISS_KEY_PREFIX = "iw_profile_nudge_dismissed_";

/**
 * Soft, dismissible reminder banner that prompts kid_explorer / solo_explorer
 * accounts to fill in basic profile info (age, grade, interests). The data
 * powers our age-appropriate content recommendations on the homepage.
 *
 * UX rules:
 *   • Only shows for explorer accounts that are MISSING `age` or `grade`.
 *   • Hidden once dismissed for the current session (sessionStorage), but
 *     re-appears on the next login until the profile is actually completed.
 *   • Never blocks the page, it's a slim banner, not a modal.
 */
const ProfileCompletionNudge = () => {
  const { user } = useApp();
  const [dismissed, setDismissed] = useState(false);
  const [pct, setPct] = useState(null);
  const [missing, setMissing] = useState(null);

  // Per-session dismissal, keyed by user.id so different family members on
  // the same device each get their own dismiss state.
  useEffect(() => {
    if (!user) return;
    const k = DISMISS_KEY_PREFIX + user.id;
    setDismissed(sessionStorage.getItem(k) === "1");
  }, [user]);

  // Compute completion %. Prefer the canonical `profile_completion_percent`
  // (Solo/Parent/Kid all write this via the unified `compute_profile_completion`
  // helper). Fall back to legacy `profile_completion_pct`, then to a coarse
  // age+grade derivation. Also captures `profile_completion_missing` so the
  // banner can surface the exact items left ("Add a Nickname & Sticker to hit
  // 100%") instead of a generic "tell us about yourself" prompt.
  useEffect(() => {
    if (!user) return;
    // Seed from whatever the auth payload has so the banner renders fast.
    const seedPct = typeof user.profile_completion_percent === "number"
      ? user.profile_completion_percent
      : (typeof user.profile_completion_pct === "number" ? user.profile_completion_pct : null);
    if (seedPct !== null) setPct(seedPct);
    else {
      let p = 0;
      if (user.age && user.age > 0) p += 50;
      if (user.grade) p += 50;
      setPct(p);
    }
    if (Array.isArray(user.profile_completion_missing)) {
      setMissing(user.profile_completion_missing);
    }

    // Always reconcile against the canonical /api/profile/{id} response -
    // it auto-heals legacy docs via `compute_profile_completion` and is
    // the only place that returns `profile_completion_missing` for both
    // kids and solos. Best-effort; banner still works without it.
    axios.get(`${API}/profile/${user.id}`).then(r => {
      if (typeof r.data?.profile_completion_percent === "number") {
        setPct(r.data.profile_completion_percent);
      }
      if (Array.isArray(r.data?.profile_completion_missing)) {
        setMissing(r.data.profile_completion_missing);
      }
    }).catch(() => {});
  }, [user]);

  if (!user) return null;
  // Parent / admin accounts get the existing Add-Explorer nudge, this banner
  // is for the kid + solo explorer flows that skip that modal.
  const isExplorer = user.role === "child_explorer" || (user.account_type || "").toLowerCase() === "solo_explorer";
  if (!isExplorer) return null;
  // Canonical "profile done" flag from /api/profile/setup. Once this flips
  // true we must not re-show the banner, even if `age` / `grade` legacy
  // fields are missing on the user object (the unified setup flow no
  // longer asks for them directly).
  if (user.profile_setup_complete === true) return null;
  // Defensive fallback, if the server didn't merge `profile_setup_complete`
  // for any reason (stale session, Google-cookie path, etc.) but the client
  // clearly has a populated profile (sticker + nickname or username), treat
  // that as done. Prevents the banner from lingering forever after setup.
  if (user.sticker && (user.nickname || user.username)) return null;
  if (dismissed) return null;
  if (pct === null) return null;
  if (pct >= 100) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY_PREFIX + user.id, "1"); } catch (_) {}
  };

  // Build the headline + supporting copy. When we know exactly what's
  // missing, name those items so the user knows what to do; otherwise
  // fall back to the friendly generic prompt.
  const friendlyMissing = (() => {
    if (!Array.isArray(missing) || missing.length === 0) return null;
    if (missing.length === 1) return `Add your ${missing[0]} to hit 100%.`;
    if (missing.length === 2) return `Add your ${missing[0]} & ${missing[1]} to hit 100%.`;
    const head = missing.slice(0, -1).join(", ");
    return `${missing.length} steps left: ${head}, and ${missing[missing.length - 1]}.`;
  })();

  return (
    <div
      className="rounded-2xl p-3.5 border flex items-center gap-3 text-sm"
      style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(6,182,212,0.06))", borderColor: "rgba(16,185,129,0.30)" }}
      data-testid="profile-completion-nudge"
    >
      <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
        <Sparkles size={16} className="text-emerald-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm" data-testid="profile-nudge-title">
          {pct === 0 ? "Tell us about yourself" : `Your profile is ${pct}% done`}
        </p>
        <p className="text-white/60 text-[12px] leading-snug" data-testid="profile-nudge-message">
          {friendlyMissing || "Add your age, grade and interests so we can recommend games and stories you'll love."}
        </p>
      </div>
      <Link
        to="/profile/setup"
        className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 text-emerald-200 text-[11px] font-bold whitespace-nowrap"
        data-testid="profile-nudge-cta"
      >
        Complete profile
      </Link>
      <button
        onClick={dismiss}
        className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
        aria-label="Dismiss"
        data-testid="profile-nudge-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

// Optional: helper that other pages can call to re-fetch profile_completion_pct
// from the server if the user just edited it.
export const refreshProfileCompletion = async (userId) => {
  try {
    const r = await axios.get(`${API}/profile/me`);
    return r.data?.profile_completion_pct;
  } catch (_) { return null; }
};

export default ProfileCompletionNudge;
